import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Brain, ThumbsUp, ThumbsDown, CheckCircle, AlertCircle, TrendingUp } from "lucide-react";

export default function CandidateReviewPanel({ candidates, loading, onApprove, onReject }) {
  return (
    <Card className="mt-6" data-testid="candidate-review-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-primary" />
              AI Shortlist Recommendations
            </CardTitle>
            <CardDescription>Review AI-generated candidate recommendations</CardDescription>
          </div>
          <Badge variant="outline" className="text-sm" data-testid="pending-count-badge">
            {candidates.length} Pending
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {candidates.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Brain className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No candidates pending review</p>
            <p className="text-sm mt-1">AI recommendations will appear here after test completion</p>
          </div>
        ) : (
          <div className="space-y-4">
            {candidates.map((candidate) => {
              const rec = candidate.ai_recommendation;
              return (
                <div
                  key={candidate.id}
                  className="p-5 border rounded-lg bg-white hover:shadow-md transition-shadow"
                  data-testid={`candidate-review-${candidate.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg">{candidate.name}</h4>
                      <p className="text-sm text-gray-600">{candidate.email}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge
                        className={rec?.recommended ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}
                        data-testid="recommendation-badge"
                      >
                        {rec?.recommended ? "✓ Recommended" : "Review Needed"}
                      </Badge>
                      <div className="flex items-center gap-2 text-sm">
                        <TrendingUp className="w-4 h-4 text-primary" />
                        <span className="font-semibold">{candidate.test_score}%</span>
                      </div>
                    </div>
                  </div>

                  {rec && (
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-600">AI Confidence:</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-xs">
                          <div
                            className="bg-primary h-2 rounded-full"
                            style={{ width: `${rec.confidence * 100}%` }}
                          />
                        </div>
                        <span className="font-medium">{Math.round(rec.confidence * 100)}%</span>
                      </div>

                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700 leading-relaxed">{rec.reasoning}</p>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        {rec.key_strengths?.length > 0 && (
                          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                            <h5 className="text-xs font-semibold text-green-800 mb-2 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Strengths
                            </h5>
                            <ul className="text-xs text-green-700 space-y-1">
                              {rec.key_strengths.map((strength) => (
                                <li key={`strength-${strength.substring(0, 20)}`}>• {strength}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {rec.concerns?.length > 0 && (
                          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <h5 className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> Concerns
                            </h5>
                            <ul className="text-xs text-amber-700 space-y-1">
                              {rec.concerns.map((concern) => (
                                <li key={`concern-${concern.substring(0, 20)}`}>• {concern}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-3 border-t">
                    <Button
                      onClick={() => onApprove(candidate.id)}
                      disabled={loading}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      data-testid="approve-button"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <ThumbsUp className="w-4 h-4 mr-2" />
                          Approve & Shortlist
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => onReject(candidate.id)}
                      disabled={loading}
                      variant="outline"
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      data-testid="reject-button"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <ThumbsDown className="w-4 h-4 mr-2" />
                          Reject
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
